// Approval roll-up for the Rate Card Builder.
//
// The owner (Andy) has to be able to open the Rate Card menu and see, without
// opening anything, which product templates are ratified and which are waiting
// on him. Every card carries a bookkeeping `status`; this module rolls a set of
// cards up into ONE state per rail row, and rolls the rows up into the
// approval queue shown at the top of the rail.
//
// Pure functions only — no React, no API. The status vocabulary is fixed by the
// backend (`devised` | `confirmed` | `needs_adjustment` | `candidate`) and is
// not extended here.

export const APPROVAL_STATES = {
  APPROVED: "approved",
  NEEDS_APPROVAL: "needs_approval",
  CHECK_RATES: "check_rates",
  NEEDS_RATES: "needs_rates",
};

/** Statuses that mean "a human still has to sign this off". */
export const PENDING_APPROVAL_STATUSES = ["devised", "candidate"];

const PENDING = new Set(PENDING_APPROVAL_STATUSES);

/**
 * A card with no stored status is treated as `devised` — the model default and
 * the safe reading: unreviewed until someone says otherwise.
 */
export function cardApprovalStatus(card) {
  return (card && card.status) || "devised";
}

export const APPROVAL_LABELS = {
  [APPROVAL_STATES.APPROVED]: "Approved",
  [APPROVAL_STATES.NEEDS_APPROVAL]: "Needs approval",
  [APPROVAL_STATES.CHECK_RATES]: "Check rates",
  [APPROVAL_STATES.NEEDS_RATES]: "Needs rates",
};

/** One-line plain-English meaning, used as the badge tooltip. */
export const APPROVAL_HINTS = {
  [APPROVAL_STATES.APPROVED]: "Every card here is confirmed. Trust it.",
  [APPROVAL_STATES.NEEDS_APPROVAL]:
    "Rates are set but nobody has signed them off yet.",
  [APPROVAL_STATES.CHECK_RATES]:
    "Validation flagged a gap — usable, but review the rates.",
  [APPROVAL_STATES.NEEDS_RATES]: "No rates yet for this product template.",
};

/**
 * Roll a list of labor rate cards up into a single row state.
 *
 * Rules, most urgent first:
 *   no cards at all           → NEEDS_RATES   ("Needs rates")
 *   any devised or candidate  → NEEDS_APPROVAL("Needs approval")
 *   any needs_adjustment      → CHECK_RATES   ("Check rates")
 *   otherwise (all confirmed) → APPROVED      ("Approved")
 *
 * @returns {{state: string, label: string, hint: string, total: number,
 *            pendingCount: number}}
 */
export function rollUpApproval(cards) {
  const list = Array.isArray(cards) ? cards : [];
  const total = list.length;

  if (total === 0) {
    return {
      state: APPROVAL_STATES.NEEDS_RATES,
      label: APPROVAL_LABELS[APPROVAL_STATES.NEEDS_RATES],
      hint: APPROVAL_HINTS[APPROVAL_STATES.NEEDS_RATES],
      total: 0,
      pendingCount: 0,
    };
  }

  const statuses = list.map(cardApprovalStatus);
  const pendingCount = statuses.filter((s) => PENDING.has(s)).length;

  let state;
  if (pendingCount > 0) {
    state = APPROVAL_STATES.NEEDS_APPROVAL;
  } else if (statuses.some((s) => s === "needs_adjustment")) {
    state = APPROVAL_STATES.CHECK_RATES;
  } else {
    state = APPROVAL_STATES.APPROVED;
  }

  return {
    state,
    label: APPROVAL_LABELS[state],
    hint: APPROVAL_HINTS[state],
    total,
    pendingCount,
  };
}

/**
 * The status of the first card in the set that is still waiting on approval —
 * so the editor banner can quote that status' own wording (a `candidate` card
 * reads differently from a `devised` one). Null when nothing is pending.
 */
export function pendingStatusOf(cards) {
  const list = Array.isArray(cards) ? cards : [];
  return list.map(cardApprovalStatus).find((s) => PENDING.has(s)) || null;
}

/** True when this row is on the owner's action list. */
export function isPendingApproval(rollup) {
  return !!rollup && rollup.state === APPROVAL_STATES.NEEDS_APPROVAL;
}

/**
 * Roll the rail's rows up into the approval queue strip.
 *
 * @param entries [{ key, label, rollup }]
 * @returns {{pendingCount: number, cardCount: number, first: object|null,
 *            pendingKeys: Set<string>}}
 */
export function summarizeApprovalQueue(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const pending = list.filter((e) => isPendingApproval(e && e.rollup));
  return {
    pendingCount: pending.length,
    cardCount: pending.reduce(
      (sum, e) => sum + (e.rollup.pendingCount || 0),
      0,
    ),
    first: pending[0] || null,
    pendingKeys: new Set(pending.map((e) => e.key)),
  };
}

/** Headline for the queue strip. Singular/plural handled here, not in JSX. */
export function approvalQueueHeadline(pendingCount) {
  if (!pendingCount) return "All rate cards approved";
  return pendingCount === 1
    ? "1 card family needs your approval"
    : `${pendingCount} card families need your approval`;
}
